import { createContext, useContext, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { AuthState, UserProfile } from '@/middleware/AuthMiddleware';

// UserProfile interface is now imported from AuthMiddleware

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string, rememberMe: boolean) => Promise<any>;
  signUp: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  logout: () => Promise<{ success: boolean; error: string | null }>;
  refreshSession: () => Promise<void>;
  updateAuthState: (authState: AuthState) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  isAdmin: false,
  isAuthenticated: false,
  isLoading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  logout: async () => ({ success: false, error: null }),
  refreshSession: async () => {},
  updateAuthState: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    isAdmin: false,
    isAuthenticated: false,
    isLoading: true,
  });
  const navigate = useNavigate();

  // Update auth state from middleware
  const updateAuthState = (newAuthState: AuthState) => {
    console.log('🔐 AuthContext: Updating auth state from middleware:', newAuthState);
    setAuthState(newAuthState);
  };

  // Auth initialization is now handled by AuthMiddleware

  const signIn = async (email: string, password: string, rememberMe: boolean) => {
    console.log('🔐 AuthContext: Attempting sign in with:', { email, hasPassword: !!password });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('🔐 AuthContext: Sign in error:', error);
        throw error;
      }

      console.log('✅ AuthContext: Sign in successful:', data.user?.email);

      // Profile will be loaded automatically by auth state change listener
      return data;

    } catch (error) {
      console.error('🔐 AuthContext: Sign in failed:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    console.log('Attempting sign up with:', { email, hasPassword: !!password });
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/portal`
      }
    });
    
    if (error) {
      console.error('Supabase signup error:', error);
      throw error;
    }
    
    console.log('Sign up successful:', data);
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    navigate('/portal/auth');
  };

  const logout = async () => {
    try {
      await signOut();
      // Clear any stored persistence preferences
      localStorage.removeItem('supabase.auth.persistence');
      sessionStorage.removeItem('supabase.auth.persistence');
      return { success: true, error: null };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const refreshSession = async () => {
    try {
      console.log('🔐 AuthContext: Manually refreshing session...');
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('🔐 AuthContext: Manual session refresh failed:', error);
        await supabase.auth.signOut();
      } else {
        console.log('✅ AuthContext: Manual session refresh successful');
      }
    } catch (error) {
      console.error('🔐 AuthContext: Manual refresh error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{
      ...authState,
      signIn,
      signUp,
      signOut,
      logout,
      refreshSession,
      updateAuthState
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 